import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  formatCurrency,
  get_request_api,
  RequestData,
  StatementModel,
  useLocationHook
} from '@velocity/shared'
import dayjs from 'dayjs'
import { SummaryCard } from '../../../component/SummaryCard'
import { faFileInvoiceDollar, faPrint } from '@fortawesome/free-solid-svg-icons'
import { Header } from '../../../component/table/Header'
import Table from '../../../component/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/free-regular-svg-icons'
import { Button2 } from '../../../component/Button'
import { useReactToPrint } from 'react-to-print'
import { AltLetterHead, LetterHead } from '../../../component/LetterHead'
import { useParams } from 'react-router-dom'
import axios from 'axios'

export default function AccountStatement({
  locationId,
  statementId
}: {
  locationId?: string
  statementId?: string
}) {
  const params = useParams()
  const [guest, setGuest] = useState(false)
  useEffect(() => {
    if (params.credentials && params.statement_id) {
      localStorage.setItem('basicToken', params.credentials)
      axios.defaults.headers.common['Authorization'] =
        'Basic ' + params.credentials
      setGuest(true)
    }
  }, [params.credentials, params.statement_id])
  const [id, setId] = useState<string>()
  useEffect(() => {
    if (params.statement_id) setId(statementId || params.statement_id)
  }, [params.statement_id, statementId])

  const contentRef = useRef<HTMLDivElement>(null)
  const [filter, setFilter] = useState({
    location_id: locationId,
    start_date: dayjs().startOf('year').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD')
  })
  useEffect(() => {
    setFilter((prev) => ({ ...prev, location_id: locationId }))
  }, [locationId])
  const { data: statementData, isPending } = useQuery({
    queryKey:
      id ?
        [`location/account-statement/${id}`]
      : [`location/${locationId}/account-statement`, { filter }],
    queryFn: get_request_api as (data: RequestData) => Promise<StatementModel>,
    enabled: !!locationId || !!id
  })

  const columns = [
    {
      key: 'date',
      title: 'Date',
      render: (value: any) => (value ? dayjs(value).format('ll') : '')
    },
    { key: 'description', title: 'Description' },
    {
      key: 'amount',
      title: 'Charges',
      render: (value: any) => (value ? formatCurrency(value) : '')
    },
    {
      key: 'payment',
      title: 'Payments',
      render: (value: any) => (value ? formatCurrency(value) : '')
    },
    {
      key: 'balance',
      title: 'Balance',
      render: (value: any) => formatCurrency(value)
    }
  ]
  const printFn = useReactToPrint({
    contentRef,
    documentTitle: `Account Statement - ${statementData?.client?.name || locationId}`
  })
  const { generateAccountStatement, sendAccountStatement } = useLocationHook()

  return (
    <div ref={contentRef}>
      <div className='mx-2'>
        <LetterHead />
        <AltLetterHead />
        <Header
          title=''
          model='invoices'
          searchAble={false}
          filterAble={false}
          searchFilter={{
            fields: [
              {
                name: 'from',
                label: 'From',
                type: 'date',
                required: true,
                initialValue: filter.start_date,
                disabled: !!id
              },
              {
                name: 'to',
                label: 'To',
                type: 'date',
                required: true,
                disabled: !!id
              }
            ],
            onUpdate:
              id ? undefined : (
                (value) =>
                  setFilter((prev) => ({
                    ...prev,
                    start_date: value.from,
                    end_date: value.to
                  }))
              ),
            loading: isPending
          }}
        />
        {statementData && (
          <>
            <div className='mt-2 flex justify-end gap-2 no-print'>
              <Button2
                title='Print'
                onClick={printFn}
                rightIcon={<FontAwesomeIcon icon={faPrint} />}
                btnStyle='outline'
              />
              {!guest ?
                <Button2
                  title={id ? 'Send' : 'Save & Send'}
                  onClick={async () => {
                    id ?
                      sendAccountStatement.mutate(id)
                    : generateAccountStatement
                        .mutateAsync({
                          location_id: locationId!,
                          start_date: filter.start_date,
                          end_date: filter.end_date
                        })
                        .then((res: any) => setId(res?.id))
                  }}
                  loading={
                    sendAccountStatement.isPending ||
                    generateAccountStatement.isPending
                  }
                  rightIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                />
              : ''}
            </div>
            <div className='font-[400] flex flex-col gap-1 mb-3'>
              <p> {statementData?.client?.name} </p>
              <p className='text-slate-500 text-[14px]'>
                {statementData?.client?.address}
              </p>
              <p className='text-slate-500 text-[14px]'>
                {statementData?.client?.email}
              </p>
              <p className='text-slate-500 text-[14px]'>
                {statementData?.client?.phone}
              </p>
            </div>

            {/* Summary Cards */}
            <div className='my-3 grid grid-cols-1 gap-4 sm:grid-cols-3'>
              <SummaryCard
                title='Total Invoiced'
                subTitle={formatCurrency(
                  statementData?.summary?.total_invoiced
                )}
                color='blue'
                icon={faFileInvoiceDollar}
              />
              <SummaryCard
                title='Total Paid'
                subTitle={formatCurrency(statementData?.summary?.total_paid)}
                color='green'
                icon={faFileInvoiceDollar}
              />
              <SummaryCard
                title='Balance Due'
                subTitle={formatCurrency(statementData?.summary?.balance)}
                color={statementData?.summary?.balance ? 'red' : 'green'}
                icon={faFileInvoiceDollar}
              />
            </div>
            <Table
              columns={columns}
              data={statementData?.transactions}
              isLoading={isPending}
              selectedRows={[]}
              footer={{
                key: 'Total',
                amount: statementData?.summary?.total_invoiced,
                payment: statementData?.summary?.total_paid,
                balance: statementData?.summary?.balance
              }}
            />
            <div className='text-center text-sm italic my-2'>
              Generated:{' '}
              {dayjs(statementData.metadata?.generated_at).format('lll')}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
